import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Box,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Autocomplete,
  InputAdornment,
  Tooltip,
  Avatar
} from '@mui/material';
import { 
  Delete, 
  Add, 
  Save, 
  Cancel, 
  ArrowBack, 
  CheckCircle, 
  Error as ErrorIcon,
  Person,
  RemoveCircle,
  Search
} from '@mui/icons-material';
import api from '../../services/api';

const CreateCommissionRequest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allApplicants, setAllApplicants] = useState([]);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formData, setFormData] = useState({
    agentId: '',
    transDate: new Date().toISOString().split('T')[0],
    receiptNo: '',
    creditAmount: '',
    remark: ''
  });
  const [deductions, setDeductions] = useState([]);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Get selected IDs from navigation state
  const selectedIds = location.state?.selectedIds || [];

  useEffect(() => {
    console.log('Selected IDs from navigation:', selectedIds);
    
    if (!selectedIds || selectedIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'No applicants selected. Please select applicants first.',
        severity: 'warning'
      });
      setTimeout(() => navigate('/applicants'), 2000);
      return;
    }

    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch all applicants for dropdown - REMOVED /api/ prefix
      console.log('Fetching all applicants...');
      const allApplicantsResponse = await api.get('/applicants');
      console.log('All applicants response:', allApplicantsResponse.data);
      
      if (allApplicantsResponse.data && Array.isArray(allApplicantsResponse.data)) {
        setAllApplicants(allApplicantsResponse.data);
      }

      // Fetch selected applicants for the commission request - REMOVED /api/ prefix
      console.log('Fetching selected applicants with IDs:', selectedIds);
      const selectedResponse = await api.post('/applicants/by-ids', selectedIds);
      
      console.log('Selected Response:', selectedResponse.data);
      
      if (selectedResponse.data && Array.isArray(selectedResponse.data)) {
        setSelectedApplicants(selectedResponse.data);
        console.log('Selected Applicants (Base for Commission):', selectedResponse.data);
      } else {
        console.warn('No applicants returned from API');
        setSelectedApplicants([]);
      }

      // Fetch agents - REMOVED /api/ prefix
      console.log('Fetching agents...');
      const agentsResponse = await api.get('/companies/agents');
      console.log('Agents response:', agentsResponse.data);
      
      if (agentsResponse.data && Array.isArray(agentsResponse.data)) {
        setAgents(agentsResponse.data);
      }
      
    } catch (error) {
      console.error('Error fetching initial data:', error);
      let errorMessage = 'Failed to load required data.';
      
      if (error.response) {
        const responseData = error.response.data;
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.errors) {
          const errorsObj = responseData.errors;
          const errorMessages = Object.values(errorsObj).flat();
          errorMessage = errorMessages.join(', ');
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message || 'An error occurred';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDateChange = (e) => {
    setFormData(prev => ({
      ...prev,
      transDate: e.target.value
    }));
    if (errors.transDate) {
      setErrors(prev => ({ ...prev, transDate: '' }));
    }
  };

  const handleAddDeduction = () => {
    // Check if there are any applicants available (from all applicants)
    const usedApplicantIds = deductions.map(d => d.applicantId);
    const availableApplicants = allApplicants.filter(
      app => !usedApplicantIds.includes(app.id || app.ID)
    );

    if (availableApplicants.length === 0) {
      setSnackbar({
        open: true,
        message: 'No more applicants available for deductions',
        severity: 'warning'
      });
      return;
    }

    // Allow multiple deductions per applicant
    setDeductions(prev => [
      ...prev,
      {
        id: Date.now(),
        applicantId: '',
        applicantName: '',
        passportNo: '',
        debitAmount: '',
        reason: '',
        sno: prev.length + 1
      }
    ]);
  };

  const handleRemoveDeduction = (id) => {
    setDeductions(prev => {
      const filtered = prev.filter(d => d.id !== id);
      return filtered.map((d, index) => ({
        ...d,
        sno: index + 1
      }));
    });
  };

  const handleDeductionApplicantChange = (id, applicant) => {
    if (!applicant) {
      // Clear the selection
      setDeductions(prev => prev.map(d =>
        d.id === id ? {
          ...d,
          applicantId: '',
          applicantName: '',
          passportNo: ''
        } : d
      ));
      return;
    }

    setDeductions(prev => prev.map(d =>
      d.id === id ? {
        ...d,
        applicantId: applicant.id || applicant.ID,
        applicantName: applicant.name || applicant.fullName || applicant.FullName,
        passportNo: applicant.passport || applicant.passportNo || applicant.PassportNo
      } : d
    ));
  };

  const handleDeductionChange = (id, field, value) => {
    setDeductions(prev => prev.map(d =>
      d.id === id ? { ...d, [field]: value } : d
    ));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.agentId) {
      newErrors.agentId = 'Agent is required';
    }
    if (!formData.transDate) {
      newErrors.transDate = 'Transaction date is required';
    }
    if (!formData.creditAmount || parseFloat(formData.creditAmount) <= 0) {
      newErrors.creditAmount = 'Valid credit amount is required';
    }
    if (selectedApplicants.length === 0) {
      newErrors.selectedApplicants = 'At least one applicant must be selected for the commission request';
    }

    // Validate deductions
    let hasInvalidDeduction = false;
    deductions.forEach((d, index) => {
      if (!d.applicantId) {
        newErrors[`deduction_applicant_${index}`] = 'Please select an applicant';
        hasInvalidDeduction = true;
      }
      if (!d.debitAmount || parseFloat(d.debitAmount) <= 0) {
        newErrors[`deduction_amount_${index}`] = 'Valid debit amount is required';
        hasInvalidDeduction = true;
      }
    });

    if (hasInvalidDeduction) {
      newErrors.deductions = 'Please fix deduction errors';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix all validation errors',
        severity: 'error'
      });
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleSubmit = async () => {
    setShowConfirmDialog(false);
    setSaving(true);
    
    try {
      const payload = {
        agentId: formData.agentId,
        transDate: formData.transDate,
        receiptNo: formData.receiptNo || undefined,
        creditAmount: parseFloat(formData.creditAmount),
        remark: formData.remark || undefined,
        applicantIds: selectedApplicants.map(a => a.id || a.ID),
        deductions: deductions.map(d => ({
          applicantId: d.applicantId,
          debitAmount: parseFloat(d.debitAmount),
          reason: d.reason || undefined,
          sno: d.sno
        }))
      };

      console.log('Submitting payload:', JSON.stringify(payload, null, 2));

      const response = await api.post('/commissionrequest', payload);

      setSnackbar({
        open: true,
        message: 'Commission request created successfully!',
        severity: 'success'
      });

      setTimeout(() => {
        navigate('/commissions/list', { state: { newRequest: response.data } });
      }, 2000);

    } catch (error) {
      console.error('Error creating commission request:', error);
      
      let errorMessage = 'Failed to create commission request';
      
      if (error.response) {
        const responseData = error.response.data;
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.errors) {
          const errorsObj = responseData.errors;
          const errorMessages = Object.values(errorsObj).flat();
          errorMessage = errorMessages.join(', ');
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message || 'An error occurred';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (saving) return;
    navigate('/applicants');
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 1 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            Create Commission Request
          </Typography>
          <Chip 
            icon={<Person />}
            label={`${selectedApplicants.length} Applicants`} 
            color="primary" 
            variant="outlined"
          />
          <Chip 
            label={`${deductions.length} Deductions`} 
            color="secondary" 
            variant="outlined"
            sx={{ ml: 1 }}
          />
        </Box>

        <Divider sx={{ mb: 0 }} />

        <Grid container spacing={4}>
          {/* Left Column - Main Form */}
          <Grid item xs={12} md={8}>
            {/* Selected Applicants Section - BASE FOR COMMISSION REQUEST */}
            <Card variant="outlined" sx={{ mb: 3, borderColor: '#1976d2', borderWidth: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" color="primary">
                    Applicants for Commission Request (Base)
                  </Typography>
                  <Chip 
                    label={`${selectedApplicants.length} Selected`} 
                    color="primary" 
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </Box>
                
                {selectedApplicants.length === 0 ? (
                  <Alert severity="warning">No applicants selected for this commission request.</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell width="5%">#</TableCell>
                          <TableCell width="40%">Full Name</TableCell>
                          <TableCell width="30%">Passport No</TableCell>
                          <TableCell width="25%">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedApplicants.map((applicant, index) => {
                          const hasDeduction = deductions.some(d => d.applicantId === (applicant.id || applicant.ID));
                          return (
                            <TableRow key={applicant.id || applicant.ID}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {applicant.name || applicant.fullName || applicant.FullName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {applicant.passport || applicant.passportNo || applicant.PassportNo}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {hasDeduction ? (
                                  <Chip 
                                    label="Has Deduction" 
                                    color="warning" 
                                    size="small"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip 
                                    label="No Deduction" 
                                    color="success" 
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {errors.selectedApplicants && (
                  <FormHelperText error>{errors.selectedApplicants}</FormHelperText>
                )}
                {/*
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    These applicants will receive the commission. Deductions can be added for any applicant from the full list.
                  </Typography>
                */}
              </CardContent>
            </Card>

            {/* Commission Details */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Commission Details
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.agentId}>
                      <InputLabel id="agent-select-label">Agent *</InputLabel>
                      <Select
                        labelId="agent-select-label"
                        name="agentId"
                        value={formData.agentId}
                        onChange={handleInputChange}
                        label="Agent *"
                      >
                        <MenuItem value="">
                          <em>Select Agent</em>
                        </MenuItem>
                        {agents.map((agent) => (
                          <MenuItem key={agent.companyID} value={agent.companyID}>
                            {agent.companyName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.agentId && (
                        <FormHelperText error>{errors.agentId}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Transaction Date *"
                      name="transDate"
                      value={formData.transDate}
                      onChange={handleDateChange}
                      error={!!errors.transDate}
                      helperText={errors.transDate}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Receipt No"
                      name="receiptNo"
                      value={formData.receiptNo}
                      onChange={handleInputChange}
                      placeholder="Leave empty for auto-generation"
                      helperText="If left empty, receipt number will be auto-generated"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Credit Amount *"
                      name="creditAmount"
                      type="number"
                      value={formData.creditAmount}
                      onChange={handleInputChange}
                      error={!!errors.creditAmount}
                      helperText={errors.creditAmount}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Remark"
                      name="remark"
                      value={formData.remark}
                      onChange={handleInputChange}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Deductions */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '95%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Deductions
                  </Typography>
                  <Tooltip title="Add deduction for any applicant">
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAddDeduction}
                        disabled={allApplicants.length === 0 || saving}
                      >
                        Add
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                {deductions.length === 0 ? (
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                    <Typography variant="body2" color="textSecondary">
                      No deductions added.
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Add deductions for any applicant (not just selected ones)
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                    {deductions.map((deduction, index) => (
                      <Card 
                        key={deduction.id} 
                        variant="outlined" 
                        sx={{ mb: 2, p: 2, position: 'relative' }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="caption" color="textSecondary">
                            #{deduction.sno}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveDeduction(deduction.id)}
                            disabled={saving}
                          >
                            <RemoveCircle fontSize="small" />
                          </IconButton>
                        </Box>

                        <Autocomplete
                          size="small"
                          fullWidth
                          options={allApplicants}
                          getOptionLabel={(option) => 
                            `${option.name || option.fullName || option.FullName} (${option.passport || option.passportNo || option.PassportNo})`
                          }
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box>
                                <Typography variant="body2">
                                  {option.name || option.fullName || option.FullName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Passport: {option.passport || option.passportNo || option.PassportNo}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Applicant *"
                              error={!!errors[`deduction_applicant_${index}`]}
                              helperText={errors[`deduction_applicant_${index}`]}
                              placeholder="Search by name or passport"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment position="start">
                                      <Search fontSize="small" />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                )
                              }}
                            />
                          )}
                          value={deduction.applicantId ? allApplicants.find(a => 
                            (a.id || a.ID) === deduction.applicantId
                          ) : null}
                          onChange={(_, newValue) => {
                            if (newValue) {
                              handleDeductionApplicantChange(deduction.id, newValue);
                            }
                          }}
                          disabled={saving}
                          sx={{ mb: 2 }}
                          isOptionEqualToValue={(option, value) => 
                            (option.id || option.ID) === (value.id || value.ID)
                          }
                        />

                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          label="Debit Amount *"
                          value={deduction.debitAmount}
                          onChange={(e) => handleDeductionChange(
                            deduction.id,
                            'debitAmount',
                            e.target.value
                          )}
                          error={!!errors[`deduction_amount_${index}`]}
                          helperText={errors[`deduction_amount_${index}`]}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                          disabled={saving}
                          sx={{ mb: 1 }}
                        />

                        <TextField
                          size="small"
                          fullWidth
                          label="Reason"
                          value={deduction.reason}
                          onChange={(e) => handleDeductionChange(
                            deduction.id,
                            'reason',
                            e.target.value
                          )}
                          placeholder="Enter reason (optional)"
                          disabled={saving}
                        />
                      </Card>
                    ))}
                  </Box>
                )}

                {errors.deductions && (
                  <Alert severity="error" sx={{ mt: 2 }}>{errors.deductions}</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Summary Section */}
        <Card sx={{ mt: 1, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">Base Applicants</Typography>
                <Typography variant="h6">{selectedApplicants.length}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">Deductions</Typography>
                <Typography variant="h6">{deductions.length}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">Credit Amount</Typography>
                <Typography variant="h6" color="primary">
                  ${formData.creditAmount || '0.00'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">Total Deductions</Typography>
                <Typography variant="h6" color="error">
                  ${deductions.reduce((sum, d) => sum + (parseFloat(d.debitAmount) || 0), 0).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">Net Amount</Typography>
                  <Typography variant="h5" fontWeight="bold">
                    ${(parseFloat(formData.creditAmount) || 0) - 
                      deductions.reduce((sum, d) => sum + (parseFloat(d.debitAmount) || 0), 0)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={saving}
            startIcon={<Cancel />}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
            disabled={saving || selectedApplicants.length === 0}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          >
            {saving ? 'Saving...' : 'Save Commission Request'}
          </Button>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Confirm Commission Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to create this commission request?
            <br /><br />
            <strong>Summary:</strong>
            <ul>
              <li>Base Applicants: {selectedApplicants.length}</li>
              <li>Deductions: {deductions.length}</li>
              <li>Total Amount: ${formData.creditAmount || '0.00'}</li>
              <li>Total Deductions: ${deductions.reduce((sum, d) => sum + (parseFloat(d.debitAmount) || 0), 0).toFixed(2)}</li>
              <li>Net Amount: ${(parseFloat(formData.creditAmount) || 0) - 
                deductions.reduce((sum, d) => sum + (parseFloat(d.debitAmount) || 0), 0)}
              </li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            startIcon={<CheckCircle />}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          icon={snackbar.severity === 'success' ? <CheckCircle /> : <ErrorIcon />}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateCommissionRequest;